import { Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  occupationsToFormValue,
  PurposeControlType,
  RoadOccupationForm,
  WorkPurposeControlType
} from './forms/road-occupation.form';
import { RoadOccupationFormComponent } from './components/road-occupation-form/road-occupation-form.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, map, Subscription } from 'rxjs';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ApplicationAppEntityApplicationRead, RoadOccupationAppEntityApplicationRead } from '../../../../../openapi';
import {
  selectApplication,
  selectApplicationState,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import {
  loadPurposeFeeCollection,
  loadRoadOccupationTypes,
  updateOccupations
} from '../../../../../store/actions/application.actions';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { ServiceListItem } from '../../../../../services/service.common.model';
import { selectServiceByUrl } from '../../../../../store/selectors/app.selectors';

export interface OccupationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addDecisionControls?: boolean;
  hideOccupationTypeControl?: boolean;
  hidePurposeControl?: boolean;
  hideCityControl?: boolean;
  addWorkPurposeControl?: boolean;
  workPurposeControlType?: 'select' | 'textarea';
  purposeControlType?: 'input' | 'textarea';
  addTypeOfFailureControl?: boolean;
  addSurfaceViolationControl?: boolean;
}

@Component({
  selector: 'app-occupation-data-section',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    RoadOccupationFormComponent,
    ReactiveFormsModule,
    StagesComponent,
    FormsModule
  ],
  providers: [RoadOccupationForm],
  templateUrl: './occupation-data-section.component.html'
})
export class OccupationDataSectionComponent implements OnInit, OnDestroy {

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public workPurposeControlType: WorkPurposeControlType = this.route.snapshot.data['workPurposeControlType'] || 'select';

  public purposeControlType: PurposeControlType = this.route.snapshot.data['purposeControlType'] || 'input';

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public serviceType: Signal<ServiceListItem | null> = this.store$.selectSignal(selectServiceByUrl(this.route?.parent?.snapshot?.data['contentUrl']));

  private fetchApplicationData: Subscription = this.store$.select(selectApplicationState)
    .pipe(filter(v => !v.loading))
    .subscribe((state) => {
      const occupations: Array<RoadOccupationAppEntityApplicationRead> = state.application?.roadOccupations || [];
      const value = occupationsToFormValue(occupations).at(0) || null;
      this.form.patchFormValues({
        value,
        purposeFeeCollection: state.purposeFeeCollection
      });
    });

  private fetchRoadOccupationPurpose: Subscription = this.store$.select(selectApplication)
    .pipe(
      map(v => v?.roadOccupationPurpose?.purposeId || v?.service?.roadOccupationPurposes?.at(0)?.purposeId),
      filter(v => !!v)
    ).subscribe((purposeId) => {
      if (purposeId) {
        this.store$.dispatch(loadPurposeFeeCollection({ purposeId }));
      }
    });

  constructor(
    public form: RoadOccupationForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public submitAndGoNext(): void {
    if (this.form.valid) {
      this.store$.dispatch(updateOccupations({
        application: this.application(),
        occupations: [this.form.values],
        successFnc: (applicationId) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadRoadOccupationTypes());

    if (this.route.snapshot.data['addDecisionControls']) {
      this.form.createDecisionControl();
    }
    if (this.route.snapshot.data['hideOccupationTypeControl']) {
      this.form.removeControl('occupationType');
    }
    if (this.route.snapshot.data['hidePurposeControl']) {
      this.form.removeControl('purpose');
    }
    if (this.route.snapshot.data['addTypeOfFailureControl']) {
      this.form.createTypeOfFailureControl();
    }
    if (this.route.snapshot.data['addSurfaceViolationControl']) {
      this.form.createSurfaceViolationControl();
    }
    if (this.route.snapshot.data['addWorkPurposeControl']) {
      this.form.createWorkPurposeControl();
    }
    if (this.route.snapshot.data['hideCityControl']) {
      this.form.removeControl('city');
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplicationData.unsubscribe();
    this.fetchRoadOccupationPurpose.unsubscribe();
  }

}
